/**
 * 등록 폼들이 공유하는 조각 — 라벨, 초안 배너, 자동 저장 표시.
 *
 * 일정/메모 폼과 출장 일지 폼은 저장하는 엔티티가 달라 하나로 합칠 수 없지만,
 * 사용자에게는 "종류만 다른 같은 폼" 으로 보여야 한다. 라벨 굵기나 배너 문구가
 * 서로 어긋나면 그 착각이 깨진다. 그래서 눈에 보이는 조각은 여기 한 곳에서만
 * 정의한다.
 */
import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { HistoryToggleOff as DraftIcon } from '@mui/icons-material';

/** 섹션 제목 — 필드가 무엇을 묻는지 한 줄로. */
export const SectionLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <Typography
    variant="caption"
    color="text.secondary"
    fontWeight={700}
    sx={{ display: 'block', mb: 0.75 }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
        *
      </Box>
    )}
  </Typography>
);

export interface DraftBannerProps {
  /** 초안 저장 시각(epoch ms). */
  savedAt: number;
  onResume: () => void;
  onDiscard: () => void;
}

/**
 * 초안 복구 배너.
 *
 * 자동으로 채우지 않고 묻는다 — 새로 쓰려고 열었는데 옛 내용이 들어차 있으면
 * 그게 더 혼란스럽다. 판단 근거로 저장 시각을 함께 보여 준다.
 */
export const DraftBanner: React.FC<DraftBannerProps> = ({ savedAt, onResume, onDiscard }) => (
  <Alert
    severity="info"
    icon={<DraftIcon fontSize="inherit" />}
    data-testid="draft-banner"
    action={
      <Stack direction="row" spacing={0.5}>
        <Button size="small" onClick={onResume}>
          이어쓰기
        </Button>
        <Button size="small" color="inherit" onClick={onDiscard}>
          버리기
        </Button>
      </Stack>
    }
  >
    작성 중이던 내용이 있습니다
    {` (${new Date(savedAt).toLocaleString('ko-KR', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })} 저장)`}
  </Alert>
);

/** 하단 액션바의 자동 저장 표시 — "닫아도 남는다" 를 눈으로 확인시켜 준다. */
export const DraftSavedHint: React.FC<{ savedAt: number | null }> = ({ savedAt }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ mr: 'auto' }}
    data-testid="draft-saved-hint"
  >
    {savedAt
      ? `${new Date(savedAt).toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit',
        })} 자동 저장됨`
      : '작성하면 자동 저장됩니다'}
  </Typography>
);

/** 모바일 전체화면에서도 항상 보이는 sticky 헤더 스타일. */
export const stickyTitleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  position: 'sticky',
  top: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: '1px solid',
  borderColor: 'divider',
  py: 1.5,
} as const;

/** 긴 폼을 스크롤해도 저장 버튼에 항상 닿게 하는 sticky 액션바 스타일. */
export const stickyActionsSx = {
  position: 'sticky',
  bottom: 0,
  bgcolor: 'background.paper',
  borderTop: '1px solid',
  borderColor: 'divider',
  px: { xs: 2, sm: 3 },
  py: 1.5,
  gap: 1,
} as const;
